import _ from 'underscore';
import { useMemo } from 'react';
import { EventActivity, Message } from 'botframework-directlinejs';
import { useAdsCopilotContext } from '@bingads-webui/ads-copilot-ui';

import { SummarizePluginResult } from '../types';

const MAX_AGE_FOR_TRIGGER_IN_MS = 5 * 1000;
const NO_DATA_STATUS = 'NoDataAvailable';

export type SummarizeMessageResult = {
  hasNoData: boolean | undefined;
  shouldSendFluctuationsTriggerMessage: boolean;
  summarizeMessage?: Message;
  summarizePluginResult: SummarizePluginResult;
};

export function useSummarizeMessage(replyToId: string): SummarizeMessageResult {
  const { activities } = useAdsCopilotContext() || {};

  const { summarizeMessage, summarizeEvent } = useMemo(() => {
    let newSummarizeMessage: Message | undefined;
    let newSummarizeEvent: EventActivity | undefined;

    if (activities) {
      let i = activities.length - 1;

      // find all the summarize messages with given replyToId
      while (i >= 0 && (!newSummarizeMessage || !newSummarizeEvent)) {
        const activity = activities[i];
        if ((activity as any).replyToId === replyToId) {
          if (activity.type === 'message') {
            if (_.any((activity.value as any)?.Features, (feature) => feature === 'SummarizeCampaignPerformance')) {
              // found the natural language summary
              newSummarizeMessage = activity;
            }
          } else if (activity.type === 'event' && activity.name === 'pluginResults') {
            // found the summarize plugin result
            if (_.any(activity.value, (pluginResult) => pluginResult?.Name === 'Summarize')) {
              newSummarizeEvent = activity;
            }
          }
        }

        i -= 1;
      }
    }

    return {
      summarizeMessage: newSummarizeMessage,
      summarizeEvent: newSummarizeEvent,
    };
  }, [activities, replyToId]);

  const isSummarizeMessageFromButtonClick = useMemo(() => {
    if (summarizeMessage) {
      // if user clicked on Summarize button or the Summarize starter prompt, we should have PluginContext in the response
      return (summarizeMessage.value as any)?.PluginContext?.Name === 'Summarize';
    }
    return false;
  }, [summarizeMessage]);

  const summarizePluginResult = _.find(summarizeEvent?.value, (pluginResult) => pluginResult.Name === 'Summarize');
  const hasNoData = useMemo(() => {
    if (summarizePluginResult) {
      return summarizePluginResult.Status === NO_DATA_STATUS || summarizePluginResult.Status === 'InternalError' || summarizePluginResult.Result?.title === NO_DATA_STATUS;
    }
    return undefined;
  }, [summarizePluginResult]);

  const shouldSendFluctuationsTriggerMessage = useMemo(() => {
    // only send trigger message if we have data and summarizeMessage is from button click
    if (summarizeEvent && !hasNoData && isSummarizeMessageFromButtonClick) {
      let activityTimestampMs: number = new Date(summarizeEvent.timestamp).getTime();
      if (Number.isNaN(activityTimestampMs)) {
        // TODO: Fix bug in ConversationThread where timestamp is modified
        activityTimestampMs = parseInt(summarizeEvent.timestamp, 10);
      }

      // check age of plugin result, only send fluctuations trigger message if the plugin result is new
      const activityAgeInMs = (Date.now() - activityTimestampMs);
      return activityAgeInMs < MAX_AGE_FOR_TRIGGER_IN_MS;
    }
    return false;
  }, [hasNoData, isSummarizeMessageFromButtonClick, summarizeEvent]);

  const ret = useMemo<SummarizeMessageResult>(() => ({
    hasNoData,
    shouldSendFluctuationsTriggerMessage,
    summarizeMessage,
    summarizePluginResult,
  }), [
    hasNoData,
    shouldSendFluctuationsTriggerMessage,
    summarizeMessage,
    summarizePluginResult,
  ]);

  return ret;
}
